/**
 * Canonical SmileConsent pricing — keep in sync across marketing, registration, Stripe UI, and billing.
 * Public checkout is Starter, Professional, and Premium.
 * Legacy Starter / Growth Pro / Enterprise allowances stay in the billing helpers.
 */

/** Legacy shared list — used by registration / billing where one list is still shown. */
export const SHARED_MARKETING_FEATURES = [
  '✨ Unlimited Clinicians',
  'AI-generated consent forms',
  'GDC & CQC compliant, UK-law ready',
  'Patient search (name, email, DOB, address)',
  'Multiple treatments per consent',
  'Digital signing & PDF',
  'Email support',
  'Advanced analytics & reporting',
  'Real-time compliance monitoring',
  'Free staff training session',
  'Audit trails & CQC-ready records',
  'Consent pathway support',
];

/** Landing page — tier-specific (volume + support; no per-seat gates required). */
export const STARTER_LANDING_FEATURES = [
  'AI-generated consent forms',
  'GDC & CQC compliant, UK-law ready',
  'Digital signing & PDF export',
  'Patient search (name, email, DOB)',
  'Audit trails & CQC-ready records',
  'Email support',
];

export const GROWTH_PRO_LANDING_FEATURES = [
  'Unlimited clinicians — no per-seat fees',
  'Advanced analytics & reporting',
  'Real-time compliance monitoring',
  'Multiple treatments per consent',
  'Free staff training session',
  'Priority email support (24h)',
  'Consent pathway support',
];

/** Shown with subtle highlight pills on the Growth Pro card. */
export const GROWTH_PRO_LANDING_HIGHLIGHTS = [
  'Unlimited clinicians — no per-seat fees',
  'Advanced analytics & reporting',
  'Priority email support (24h)',
];

export const ENTERPRISE_LANDING_FEATURES = [
  'Dedicated account manager',
  'Phone support & onboarding call',
  'Multi-site & group practice ready',
  'Custom pricing for large groups',
  'SLA-backed priority response',
];

/** Extra bullets on Enterprise (billing / other pages). */
export const ENTERPRISE_EXTRA_FEATURES = [
  'Dedicated account manager',
  'Priority support',
  'Advanced team collaboration',
];

export function getMarketingFeaturesForTier(tierId) {
  if (tierId === 'starter' || tierId === 'starter_v2') {
    return [...STARTER_LANDING_FEATURES];
  }
  if (tierId === 'growth-pro' || tierId === 'growthpro' || tierId === 'professional') {
    return [...STARTER_LANDING_FEATURES, ...GROWTH_PRO_LANDING_FEATURES];
  }
  if (tierId === 'enterprise' || tierId === 'premium') {
    return [
      ...STARTER_LANDING_FEATURES,
      ...GROWTH_PRO_LANDING_FEATURES,
      ...ENTERPRISE_LANDING_FEATURES,
    ];
  }
  return [...SHARED_MARKETING_FEATURES];
}

/** Landing-style rows: name, price string, consents line, features array. */
export function getLandingPricingRows() {
  return [
    {
      id: 'starter_v2',
      name: 'Starter',
      price: '£19.99/month',
      consents: '100 consents per month',
      popular: false,
      tagline: 'Perfect for solo practices',
      features: STARTER_LANDING_FEATURES,
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '£39.99/month',
      consents: '500 consents per month',
      popular: true,
      tagline: 'Chosen by most practices',
      dividerLabel: 'Everything in Starter, plus:',
      features: GROWTH_PRO_LANDING_FEATURES,
      highlightFeatures: GROWTH_PRO_LANDING_HIGHLIGHTS,
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '£69.99/month',
      consents: '1,500 consents per month',
      popular: false,
      tagline: 'For groups and multi-site practices',
      dividerLabel: 'Everything in Professional, plus:',
      customPricingNote: 'Custom pricing available for groups',
      features: ENTERPRISE_LANDING_FEATURES,
    },
  ];
}

/**
 * Dental Consent Plans page shape (ids, numeric price, colors).
 */
export const DENTAL_CONSENT_PLANS_LAYOUT = [
  {
    id: 'starter_v2',
    name: 'Starter',
    price: 19.99,
    period: 'month',
    description: 'Perfect for small practices',
    consents: '100 consents per month',
    features: getMarketingFeaturesForTier('starter_v2'),
    popular: false,
    color: 'blue',
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 39.99,
    period: 'month',
    description: 'Most popular for growing practices',
    consents: '500 consents per month',
    features: getMarketingFeaturesForTier('professional'),
    popular: true,
    color: 'green',
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 69.99,
    period: 'month',
    description: 'For large practices and groups',
    consents: '1,500 consents per month',
    features: getMarketingFeaturesForTier('premium'),
    popular: false,
    color: 'purple',
  },
];

export function getPricingComparisonPlans() {
  return DENTAL_CONSENT_PLANS_LAYOUT;
}

/**
 * Compact software-page cards — uses same tier features as comparison layout.
 */
export function getSoftwareStylePricingCards() {
  return DENTAL_CONSENT_PLANS_LAYOUT.map((plan) => ({
    id: plan.id,
    name: plan.name,
    price: `£${plan.price}`,
    period: '/month',
    description: plan.description,
    popular: plan.popular,
    consents: plan.consents,
    features: plan.features,
  }));
}

/** Registration step: same core bullets for every tier; limits shown separately in the UI. */
export const REGISTRATION_SHARED_FEATURES = [...SHARED_MARKETING_FEATURES];
